import { ApolloQueryResult } from "@apollo/client";
import { getApolloClient } from "src/clients/apollo-client";
import { GET_SCOPE_MAPPINGS_QUERY } from "src/common/gql-operations";
import { NETWORK_ONLY } from "src/common/constants";
import { TenantID } from "src/common/enums";
import { GetScopeMappingsData, ScopeMapping } from "src/models/get-scope-mappings";
import { logger } from "src/logger";

import initialMetricsPublisher from 'src/metrics';
import * as KatalMetrics from "@amzn/katal-metrics";

const cloudWatchDimensions = [new KatalMetrics.Metric.String('provider', 'scope-mapping-provider')];
const additionalMetricsContext = new KatalMetrics.Context({cloudWatchDimensions});
const graphqlClientMetricsPublisher =
    initialMetricsPublisher.newChildActionPublisherForMethod('graphql-client', additionalMetricsContext);

/**
 * Fetches the {@link ScopeMapping} for a single scope (site) via getScopeMappings.
 *
 * This is data access only — it returns the raw mapping, which carries the scope's idpType as a
 * string. Resolving that string to the IdpType enum (and deciding which MFA controls to show) is a
 * separate concern (see normalizeIdpType in common/util).
 *
 * Throws if the mapping can't be resolved — a network/GraphQL failure, a per-scope failure entry,
 * or the scope missing from the response — so the caller surfaces the problem rather than acting on
 * a fabricated default.
 *
 * @param scope the scope (site id) to look up.
 * @return the scope's {@link ScopeMapping}.
 * @throws if the mapping cannot be fetched (request failure, per-scope failure, or missing).
 */
export const getScopeMapping = async (scope: string): Promise<ScopeMapping> => {
    logger.info(`Calling getScopeMappings for scope: ${scope}`);
    graphqlClientMetricsPublisher.publishCounterMonitor('get-scope-mappings.INVOCATION', 1);

    let result: ApolloQueryResult<GetScopeMappingsData>;
    try {
        result = await getApolloClient().query<GetScopeMappingsData>({
            query: GET_SCOPE_MAPPINGS_QUERY,
            fetchPolicy: NETWORK_ONLY, // don't cache — a site's idpType can be reconfigured
            variables: {
                getScopeMappingsInput: {
                    tenantId: TenantID.AFTX,
                    scopes: [scope],
                },
            },
        });
    } catch (e) {
        // Transport / GraphQL failure — bubble up so the caller surfaces it rather than guessing.
        logger.error(`getScopeMappings request failed for scope ${scope}`, e as Error);
        graphqlClientMetricsPublisher.publishCounterMonitor('get-scope-mappings.ERROR', 1);
        throw e;
    }

    const entry = result.data?.getScopeMappings?.results?.find(r => r.scope === scope);
    if (!entry) {
        // The response never mentioned the requested scope — we can't determine its mapping.
        graphqlClientMetricsPublisher.publishCounterMonitor('get-scope-mappings.MISSING', 1);
        throw new Error(`getScopeMappings returned no entry for scope ${scope}`);
    }
    if (entry.failure) {
        graphqlClientMetricsPublisher.publishCounterMonitor('get-scope-mappings.SCOPE_FAILURE', 1);
        throw new Error(`getScopeMappings failed for scope ${scope}: ` +
            `${entry.failure.errorCode} - ${entry.failure.message}`);
    }
    if (!entry.success) {
        // Neither success nor failure — a contract violation; treat as unresolvable.
        graphqlClientMetricsPublisher.publishCounterMonitor('get-scope-mappings.MISSING', 1);
        throw new Error(`getScopeMappings returned an empty result for scope ${scope}`);
    }

    logger.info(`Resolved scope mapping for ${scope} ` +
        `(idpType=${entry.success.scopeMapping.metadata.config.idpType})`);
    graphqlClientMetricsPublisher.publishCounterMonitor('get-scope-mappings.SUCCESS', 1);
    return entry.success.scopeMapping;
};
